import {Injectable} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {mensajesModalRnec} from 'src/app/core/enums/rnec/mensaje-modal-rnec.enum';
import {IVI, IVI_MESSAGES_USUARIO} from 'src/app/core/enums/rnec/rnec.enum';
import {DocumentosDigitalesManager} from 'src/app/core/gestor-contenido-documentos/validarDocumento';
import {GeneralResponse} from 'src/app/core/models/general-response/general-response';
import {DetalleAfiliadoHijo} from 'src/app/core/models/rnec/detalleAfiliadoHijo';
import {IdentidadPersonaDto} from 'src/app/core/models/rnec/identidad-persona.dto';
import {RnecResponseDto} from 'src/app/core/models/rnec/rnec-response.dto';
import {ModalVariadoService} from 'src/app/core/services/modal-variado/modal-variado.service';
import {
  ObligatoriedadDocumentosService
} from 'src/app/core/services/obligatoriedadDocumento/obligatoriedadDocumento.service';
import {RnecService} from 'src/app/core/services/rnec/rnec.service';
import {ValidacionesFormularioService} from './validacionesFormulario.service';
import {SolicitudTiposEnum} from '../../../../../core/models/request/afiliaciones/solicitud-tipos.enum';
import {esCambioValidoDeEstadoCivil} from '../../../../../core/enums/cambio-estado-civil-enum';
import {WresponseData} from '../../../../../core/models/worker/wresponse-data';
import {environment} from '../../../../../../environments/environment';
import {LocalStorageService} from '../../../../../core/services/local-storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ConsultaRnecService {
  private contIntento = 0;
  private intentosPermitidos = 2;
  private estadoRnec: any;
  public botonContinuar = false;
  private tipoPersonaAnterior = '';
  private solicitudIdAnterior = '';
  private solicitudIdActual = '';

  constructor(
    private validacionesFormularioService: ValidacionesFormularioService,
    private _ModalVariadoService: ModalVariadoService,
    private _RnecService: RnecService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    public _ObligatoriedadDocumentosService: ObligatoriedadDocumentosService,
    private _storage: LocalStorageService
  ) {
  }

  public async realizarConsulta(
    tipoPersona: string,
    identidadBeneficiario: any,
    personaControl: any,
    form: any,
    index?: number,
    solicitudId?: string
  ): Promise<void> {
    this.solicitudIdActual = solicitudId ?? '';
    const esMismaSolicitud = solicitudId === this.solicitudIdAnterior;
    const esMismoTipoPersona = tipoPersona === this.tipoPersonaAnterior;

    if (!esMismaSolicitud || !esMismoTipoPersona) {
      this.reiniciarIntentos();
    }
    if (!this.esDocumentoValido(personaControl)) {
      this.mostrarError(tipoPersona, personaControl, form);
      this.actualizarContexto(tipoPersona, solicitudId);
      return;
    }
    if (this.alcanzoMaximoIntentos()) {
      this.handleMaxIntentos(tipoPersona, personaControl, form);
      this.actualizarContexto(tipoPersona, solicitudId);
      return;
    }
    await this.intentarValidacion(tipoPersona, identidadBeneficiario, personaControl, form);
    this.actualizarContexto(tipoPersona, solicitudId);
  }


  private async ejecutarValidacion(tipoPersona: string, identidadBeneficiario: any, personaControl: any, form: any, index?: number): Promise<void> {
    const estadoCivilCodigo = personaControl?.controls?.['estadoCivil']?.value?.codigo;
    if (estadoCivilCodigo != null) {
      const datosOrigen = this._storage.parseItem<WresponseData>(environment.LOCAL_STORAGE.TRABAJADOR_INFORMACION);
      const estadosValidos = esCambioValidoDeEstadoCivil(
        datosOrigen?.validacion_afiliado?.afiliado?.estadoCivil,
        estadoCivilCodigo
      );
      if (!estadosValidos) {
        this._ObligatoriedadDocumentosService.documentosConyugeListosSubject.next(true);
        return;
      }
    }
    const estrategiaValidacion: Record<string, () => Promise<void>> = {
      beneficiario: () => this.validarBeneficiario(personaControl, identidadBeneficiario, form),
      default: async () => {
        await this.consultaRnec(tipoPersona, identidadBeneficiario, personaControl);
      },
    };

    await (estrategiaValidacion[tipoPersona] || estrategiaValidacion.default)();
  }

  public async validarBeneficiario(
    personaControl: any,
    identidadBeneficiario: any,
    form: any,
    index?: number
  ): Promise<void> {
    const resultadoIdentidad = await this.consultaRnec('beneficiario', identidadBeneficiario, personaControl);
    const detalleAfiliadoHijo: DetalleAfiliadoHijo = {
      identidadPersonaHijo: IdentidadPersonaDto.objBeneficiarioARnc(personaControl.getRawValue()),
      identidadPersonaAfiliado: IdentidadPersonaDto.objTrabajadorARnc(form)
    };
    const validacionIdentidadExitosa = !!resultadoIdentidad?.soporteDigital && resultadoIdentidad.soporteDigital.trim() !== '';
    const esHijo = personaControl.getRawValue()?.parentescoPerCarg?.name?.toLowerCase() === 'hijo';
    const superoIntentos = this.contIntento >= this.intentosPermitidos;
    if (validacionIdentidadExitosa && esHijo) {
      await this.validarHijo(detalleAfiliadoHijo, personaControl);
    }
    if (superoIntentos || validacionIdentidadExitosa) {
      this._ObligatoriedadDocumentosService.documentosBeneficiarioListosSubject.next(true);
    }
  }

  private mostrarError(tipoPersona: string, personaControl: any, form: any): void {
    const handlers: Record<string, (control: any, tipo: string, form: FormGroup) => void> = {
      trabajador: this.mostrarErrorTitular.bind(this),
      conyuge: this.mostrarErrorConyuge.bind(this),
      beneficiario: this.mostrarErrorBeneficiario.bind(this)
    };

    handlers[tipoPersona]?.(personaControl, tipoPersona, form);
  }

  private mostrarErrorTitular(personaControl: any, tipoPersona: any): void {
    try {
      let closeModal = false;
      if(personaControl.value.TipoIdTrabajador=='CO1E' || personaControl.value.TipoIdTrabajador=='CO1D'|| personaControl.value.TipoIdTrabajador=='CO1W'){
        closeModal = true;
      }
      this.validacionesFormularioService.validarTrabajador(personaControl);
      this.validarDocumentos(tipoPersona);
      if(!closeModal){
        this._ModalVariadoService.modalError(mensajesModalRnec.datosParaDoc);
      }
      this.botonContinuar = true;
      personaControl.get('EsValido').setValue(true);
    } catch (error) {
      console.error('Error en mostrarErrorTitular:', error);
    }
  }

  private mostrarErrorConyuge(personaControl: any, tipoPersona: any): void {
    this.validacionesFormularioService.validarConyuge(personaControl);
    this.validarDocumentos(tipoPersona);
    this._ModalVariadoService.modalError(mensajesModalRnec.datosParaDoc);
    this._ObligatoriedadDocumentosService.documentosConyugeListosSubject.next(true);

    const btnConsultarConyuge = document.getElementById('btnConsultarConyuge');
    if (btnConsultarConyuge) {
      btnConsultarConyuge.style.display = 'none';
    } else {
      console.log('El botón con id "btnConsultarConyuge" no se encontró.');
    }
  }


  private mostrarErrorBeneficiario(personaControl: any, tipoPersona: any, form: any): void {
    this.validacionesFormularioService.validarParentescoBeneficiario(personaControl);
    this.validarDocumentos(tipoPersona);
    this._ModalVariadoService.modalError(mensajesModalRnec.datosParaDoc);
    personaControl.get('EsValido').setValue(true);
    this._ObligatoriedadDocumentosService.documentosBeneficiarioListosSubject.next(true);
  }


  async consultaRnec(tipoPersona: string, identidadBeneficiario: any, form: FormGroup): Promise<RnecResponseDto> {
    this._ModalVariadoService.ModalProceso('Procesando...');
    return new Promise((resolve, reject) => {
      this._RnecService.consultar(identidadBeneficiario).subscribe(
        (rnecResponse: GeneralResponse<RnecResponseDto>) => {
          this.estadoRnec = rnecResponse.body;
          if (!this._RnecService.esIdentidadValidadaExitosamente(this.estadoRnec.ivi)) {
            setTimeout(() => this.finalizarProceso(), 100);
            return resolve(this.estadoRnec);
          }
          this.handlePersonaValidation(tipoPersona, form);
          const acciones: Record<string, () => void> = {
            trabajador: () => {
              form.patchValue({base64Valid: this.estadoRnec.soporteDigital});
            },
            conyuge: () => this.actualizarGrupoConyugeBase64(form),
            beneficiario: () => this.actualizarGrupoBeneficiarioBase64(form)
          };
          acciones[tipoPersona]?.();
          setTimeout(() => this.finalizarProceso(), 100);
          resolve(this.estadoRnec);
        },
        (error) => reject(error)
      );
    });
  }

  async validarHijo(detalleAfiliadoHijo: DetalleAfiliadoHijo, form: FormGroup): Promise<RnecResponseDto> {
    return new Promise((resolve, reject) => {
      this._RnecService.validarHijo(detalleAfiliadoHijo).subscribe(
        (response: GeneralResponse<RnecResponseDto>) => {
          this.estadoRnec = response.body;
          if (response.body.validacion && response.body.soporteParentesco !== '') {
            this.realizarAccionesValidacionExitosa(form);
          } else {
            this._ObligatoriedadDocumentosService.documentosBeneficiarioListosSubject.next(true)
            this._ModalVariadoService.modalAlerta(mensajesModalRnec.errorParentesco);
          }
          resolve(this.estadoRnec);
        },
        (error) => reject(error)
      );
    });
  }

  private realizarAccionesValidacionExitosa(form: FormGroup): void {
    this.handlePersonaValidation('beneficiario', form);
    this.procesarValidacionHijoExitosa(form);
  }

  private handlePersonaValidation(tipoPersona: string, form: FormGroup) {
    const validadores = {
      trabajador: () => this.validacionesFormularioService.validarTrabajador(form),
      conyuge: () => this.validacionesFormularioService.validarConyuge(form),
      beneficiario: () => this.validacionesFormularioService.validarParentescoBeneficiario(form)
    };
    validadores[tipoPersona]();
  }

  private actualizarGrupoConyugeBase64(form: FormGroup): void {
    if (!form) return;
    const btnConsultar = document.getElementById('btnConsultarConyuge');
    btnConsultar?.style.setProperty('display', 'none');
    const conyugeGroup = form.get('conyuge') as FormGroup | null;
    const soporteDigital = this.estadoRnec?.soporteDigital ?? null;
    if (conyugeGroup) {
      conyugeGroup.get('base64Valid')?.setValue(soporteDigital);
    } else {
      form.get('base64Valid')?.setValue(soporteDigital);
    }
    this.validarDocumentos('conyuge');
    const documentos = this._ObligatoriedadDocumentosService.documentosConyuge ?? [];
    const documentosActualizados = documentos.filter(
      doc =>
        doc?.idForm !== 'documentoIdentidadConyuge' &&
        doc?.idForm !== 'documentoDeIdentidad'
    );
    if (documentosActualizados.length !== documentos.length) {
      this._ObligatoriedadDocumentosService.documentosConyuge = documentosActualizados;
      this._ObligatoriedadDocumentosService.documentosConyugeSubject.next(documentosActualizados);
      this._ObligatoriedadDocumentosService.documentosConyugeListosSubject.next(true);
    }
  }


  private actualizarGrupoBeneficiarioBase64(form: FormGroup): void {
    if (!form) return;
    if (form.contains('EsValido')) {
      form.get('EsValido')?.setValue(true);
    } else {
      form.addControl('EsValido', new FormControl(true));
    }
    if (!form.contains('base64Valid')) {
      form.addControl('base64Valid', new FormControl(null));
    }
    const soporteDigital = this.estadoRnec?.soporteDigital ?? null;
    form.patchValue({base64Valid: soporteDigital});
    this.validacionesFormularioService.validarParentescoBeneficiario(form);
    const documentos = this._ObligatoriedadDocumentosService.documentosBeneficiario ?? [];
    const documentosActualizados = documentos.filter(
      (doc: any) => doc?.idForm !== 'idBeneficiario'
    );
    if (documentosActualizados.length !== documentos.length) {
      this._ObligatoriedadDocumentosService.documentosBeneficiario = documentosActualizados;
      this._ObligatoriedadDocumentosService.documentosBeneficiarioSubject.next(documentosActualizados);
    }
  }

  private procesarValidacionHijoExitosa(form: FormGroup): void {
    form.get('EsValido')?.setValue(true);
    if (!form.contains('esHijoValido')) {
      form.addControl('esHijoValido', new FormControl(null));
    }
    if (!form.contains('base64Valid')) {
      form.addControl('base64Valid', new FormControl(null));
    }
    form.patchValue({
      esHijoValido: true,
      base64Valid: this.estadoRnec?.soporteParentesco || null
    });
    if (this.estadoRnec?.soporteParentesco) {
      // En el flujo de afiliación de beneficiarios (AFB) el documento de
      // identidad del titular (docIdeTrabajador) debe solicitarse siempre,
      // por lo que no se retira de la lista de documentos a adjuntar.
      const esAfiliacionBeneficiario = this.solicitudIdActual === SolicitudTiposEnum.AFB;
      const documentosActualizados = this._ObligatoriedadDocumentosService.documentosBeneficiario
        .filter(doc =>
          !(doc.idForm === 'regBeneficiario') &&
          !(doc.idForm === 'docIdeTrabajador' && !esAfiliacionBeneficiario)
        );
      this._ObligatoriedadDocumentosService.documentosBeneficiario = documentosActualizados;
      this._ObligatoriedadDocumentosService.documentosBeneficiarioSubject.next(documentosActualizados);
    } else {
      this._ObligatoriedadDocumentosService.documentosBeneficiarioSubject.next(
        this._ObligatoriedadDocumentosService.documentosBeneficiario
      );
    }
  }

  public async validarDocumentos(tipoPersona: any): Promise<void> {
    this.configurarDocumentosPorTipoPersona(tipoPersona);
  }

  private configurarDocumentosPorTipoPersona(tipoPersona: string): void {
    const documentosMap: { [key: string]: any } = {
      'beneficiario': this._ObligatoriedadDocumentosService.documentosBeneficiario,
      'trabajador': this._ObligatoriedadDocumentosService.documentosTitular,
      'conyuge': this._ObligatoriedadDocumentosService.documentosConyuge
    };
    const documentos = documentosMap[tipoPersona];
    if (documentos) {
      DocumentosDigitalesManager.setDocumentosSolicitud(documentos);
    }
  }

  public modalOpen(): void {
    const modalMap: { [key: string]: Function } = {
      [IVI.IVE]: () => {
        this._ModalVariadoService.modalConfirmacion(IVI_MESSAGES_USUARIO[this.estadoRnec.ivi]);
      },
      default: () => {
        this._ModalVariadoService.modalError(IVI_MESSAGES_USUARIO[this.estadoRnec.ivi]);
      }
    };

    (modalMap[this.estadoRnec.ivi] || modalMap.default)();
  }

  public finalizarProceso() {
    this.dialog.closeAll();
    this.modalOpen();
  }

  private esDocumentoValido(tipoIdentificacion: FormGroup): boolean {
    const valores = tipoIdentificacion.getRawValue();
    let tipoDocumento = '';
    if (valores?.conyuge) {
      tipoDocumento = valores?.conyuge?.TipoIdConyuge;
    } else {
      tipoDocumento = valores?.TipoIdConyuge || valores?.TipoIdTrabajador || valores?.tipoDocumento;
    }
    return this._RnecService.esTipoDocumentoParaConsultar(tipoDocumento);
  }

  public resetRNEC(): void {
    this.contIntento = 0;
    this.tipoPersonaAnterior = '';
    this.solicitudIdAnterior = '';
    this.botonContinuar = false;
    this.estadoRnec = null;
  }

  private reiniciarIntentos(): void {
    this.contIntento = 0;
  }

  private incrementarIntento(): void {
    this.contIntento++;
  }

  private alcanzoMaximoIntentos(): boolean {
    return this.contIntento >= this.intentosPermitidos;
  }

  private actualizarContexto(tipoPersona: string, solicitudId?: string): void {
    this.tipoPersonaAnterior = tipoPersona;
    this.solicitudIdAnterior = solicitudId ?? '';
  }

  private async intentarValidacion(
    tipoPersona: string,
    identidadBeneficiario: any,
    personaControl: any,
    form: any
  ): Promise<void> {
    this.incrementarIntento();

    await this.ejecutarValidacion(tipoPersona, identidadBeneficiario, personaControl, form);

    const identidadNoValida = !this._RnecService.esIdentidadValidadaExitosamente(this.estadoRnec?.ivi);

    if (identidadNoValida && this.alcanzoMaximoIntentos()) {
      this.handleMaxIntentos(tipoPersona, personaControl, form);
    }
  }


  private handleMaxIntentos(
    tipoPersona: string,
    personaControl: any,
    form: any
  ): void {
    this.reiniciarIntentos();
    this.tipoPersonaAnterior = '';
    this.botonContinuar = true;
    if (tipoPersona === 'conyuge') {
      document.getElementById('btnConsultarConyuge')?.style.setProperty('display', 'none');
      this._ObligatoriedadDocumentosService.documentosConyugeListosSubject.next(true);
    }
    this.mostrarError(tipoPersona, personaControl, form);
  }
}
 